import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { ContentSwap } from 'ui-beta/contentSwap';
import { ChevronLeftSmall } from 'ui/icons';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { colors, spacing } from 'ui/theme';
import { prefixWithUserProject } from 'utils/indexPageUtils';

import { selectWysiwigEditorEnabled } from 'core/userContext/selectors';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import { DragDropSelector, TemplateEditorSelector, TemplateNameInput } from '../../components';
import { TEMPLATE_MENU_OPTIONS } from '../../constants';
import { createTemplate } from '../../ducks/actions';

const EMAIL_MODAL_VIEWS = {
  EDITOR: 'editor',
  DRAGDROP: 'drag-drop',
};

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledChevronLeftSmall = styled(ChevronLeftSmall)`
  margin-right: ${spacing.half};
  path {
    stroke: ${colors.grey35};
    stroke-width: 0.75;
  }
`;

type ContentRecord = Record<string, string>;

type ContentTree = Record<string, ContentRecord>;

type NewEmailTemplateModalProps = {
  /* Function to Close Modal */
  closeModal: () => void;
  /* content strings to render */
  content: Record<string, unknown>;
  currentFolderId: number | null;
  /* Identifier of the template type selected */
  id: string;
};

export const NewEmailTemplateModal = ({
  closeModal = () => null,
  content,
  currentFolderId,
  id = '',
  ...props
}: NewEmailTemplateModalProps) => {
  const [templateName, setTemplateName] = useState('');
  const [selectedEditor, setSelectedEditor] = useLocalStorage(
    prefixWithUserProject('template-index-new-email-editor'),
    '',
  );
  const [selectedDragDrop, setSelectedDragDrop] = useLocalStorage(
    prefixWithUserProject('template-index-new-email-dragDrop-template'),
    '',
  );
  const [activeView, setActiveView] = useState(EMAIL_MODAL_VIEWS.EDITOR);

  const dispatch = useDispatch();
  // @ts-ignore
  const isWYSIWYGEnabled = useSelector((reduxState) => selectWysiwigEditorEnabled(reduxState));

  useEffect(() => document.getElementById('email-template-name-input')?.focus(), []);

  const {
    templateNameInput,
    // @ts-ignore
    modalHeader: { prefix, suffix },
    templateEditorSelector,
    dragDropSelector,
    continueButtonText,
    createTemplateButtonText,
  } = content;

  const shouldDisplayContinue =
    activeView !== EMAIL_MODAL_VIEWS.DRAGDROP &&
    // @ts-ignore
    activeView !== EMAIL_MODAL_VIEWS.DRAGDROP &&
    selectedEditor === (templateEditorSelector as ContentTree).dragDrop.title;
  const [mainButtonText, mainButtonAction] = shouldDisplayContinue
    ? [continueButtonText as string, () => setActiveView(EMAIL_MODAL_VIEWS.DRAGDROP)]
    : [
        createTemplateButtonText as string,
        () =>
          dispatch(
            createTemplate({
              currentFolderId,
              name: templateName,
              messageMedium: TEMPLATE_MENU_OPTIONS.email.slug,
              // this will be empty if drag drop is not selected
              // @ts-ignore
              dragDropSlug:
                selectedEditor === (templateEditorSelector as ContentTree).dragDrop.title &&
                selectedDragDrop,
            }),
          ),
      ];
  const shouldDisableMainButton =
    selectedEditor.length === 0 ||
    templateName.length === 0 ||
    templateName.length > 255 ||
    (activeView === EMAIL_MODAL_VIEWS.DRAGDROP && selectedDragDrop.length === 0);

  return (
    <Modal
      id={`email-${id}-template-modal`}
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
      data-test={`email-template-modal`}
      {...props}
    >
      <ModalHeader>
        {activeView === EMAIL_MODAL_VIEWS.DRAGDROP ? (
          <BackButton onClick={() => setActiveView(EMAIL_MODAL_VIEWS.EDITOR)}>
            <StyledChevronLeftSmall height={36} width={36} />
            Back
          </BackButton>
        ) : (
          `${prefix as string} ${TEMPLATE_MENU_OPTIONS.email.modalLabel} ${suffix as string}`
        )}
      </ModalHeader>
      <ModalBody>
        <ContentSwap activeView={activeView}>
          <div id={EMAIL_MODAL_VIEWS.EDITOR}>
            <TemplateNameInput
              content={templateNameInput}
              templateName={templateName}
              setTemplateName={setTemplateName}
              id="email-template-name-input"
            />
            <TemplateEditorSelector
              content={templateEditorSelector as ContentTree}
              setSelectedEditor={setSelectedEditor}
              selectedEditor={selectedEditor}
              isWYSIWYGEnabled={isWYSIWYGEnabled}
            />
          </div>
          <div id={EMAIL_MODAL_VIEWS.DRAGDROP}>
            <DragDropSelector
              content={dragDropSelector}
              setSelectedDragDrop={setSelectedDragDrop}
              selectedDragDrop={selectedDragDrop}
            />
          </div>
        </ContentSwap>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={mainButtonAction}
          isDisabled={shouldDisableMainButton}
          size={BUTTON_SIZES.LARGE}
        >
          <span>{mainButtonText}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
